import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Mic, Send, X } from 'lucide-react'
import { patientService } from '../../../services/patientApi'
import { useAuth } from '../../../contexts/AuthContext'
import NoteMessage from './NoteMessage'

function normalizeSpeech(value) {
  return String(value || '').replace(/\s+/g, ' ').trim()
}

function mergeSpeech(previous, incoming) {
  const base = normalizeSpeech(previous)
  const next = normalizeSpeech(incoming)
  if (!next) return base
  if (!base) return next
  const baseFold = base.toLocaleLowerCase('pt-BR')
  const nextFold = next.toLocaleLowerCase('pt-BR')
  if (
    nextFold === baseFold
    || baseFold.endsWith(` ${nextFold}`)
    || baseFold.startsWith(`${nextFold} `)
  ) return base
  if (nextFold.startsWith(`${baseFold} `)) {
    return normalizeSpeech(`${base}${next.slice(base.length)}`)
  }
  const baseWords = base.split(' ')
  const nextWords = next.split(' ')
  const max = Math.min(baseWords.length, nextWords.length, 12)
  for (let size = max; size > 0; size -= 1) {
    const tail = baseWords.slice(-size).join(' ').toLocaleLowerCase('pt-BR')
    const head = nextWords.slice(0, size).join(' ').toLocaleLowerCase('pt-BR')
    if (tail !== head) continue
    const rest = nextWords.slice(size).join(' ')
    return rest ? `${base} ${rest}` : base
  }
  return `${base} ${next}`
}

function speechRecognizer() {
  const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition
  return Ctor ? new Ctor() : null
}

function ShiftNoteSheet({ open, patientId, patientName, onClose }) {
  const { user } = useAuth()
  const [notes, setNotes] = useState([])
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [listening, setListening] = useState(false)
  const [error, setError] = useState('')
  const inputRef = useRef(null)
  const feedRef = useRef(null)
  const onCloseRef = useRef(onClose)
  const recognitionRef = useRef(null)
  const listeningRef = useRef(false)
  const textRef = useRef('')
  const sessionRef = useRef(0)
  const committedRef = useRef('')
  const sessionFinalRef = useRef('')
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open || !patientId) return undefined
    let active = true
    setText('')
    textRef.current = ''
    committedRef.current = ''
    sessionFinalRef.current = ''
    setError('')
    patientService.notes(patientId, { scope: 'today' })
      .then((response) => {
        if (active) setNotes(response.data || [])
      })
      .catch((err) => {
        if (active) setError(err.response?.data?.message || 'Não foi possível carregar as notas de hoje')
      })
    const timer = window.setTimeout(() => inputRef.current?.focus(), 280)
    const onKey = (event) => {
      if (event.key === 'Escape') onCloseRef.current?.()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      active = false
      window.clearTimeout(timer)
      document.removeEventListener('keydown', onKey)
      listeningRef.current = false
      sessionRef.current += 1
      recognitionRef.current?.stop?.()
      recognitionRef.current = null
      setListening(false)
    }
  }, [open, patientId])

  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = 0
  }, [notes.length, open])

  if (!open) return null

  const owns = (note) => user?.role === 'admin' || Number(note.author_user_id) === Number(user?.id)

  const stopListening = () => {
    listeningRef.current = false
    sessionRef.current += 1
    committedRef.current = normalizeSpeech(textRef.current)
    sessionFinalRef.current = ''
    const recognition = recognitionRef.current
    recognitionRef.current = null
    try { recognition?.stop() } catch { /* já estava parado */ }
    setListening(false)
  }

  const startListening = async () => {
    const recognition = speechRecognizer()
    if (!recognition) {
      setError('O ditado por voz não está disponível neste navegador. No iPhone, use o microfone do teclado.')
      inputRef.current?.focus()
      return
    }
    setError('')
    const token = sessionRef.current + 1
    sessionRef.current = token
    listeningRef.current = true
    setListening(true)
    try {
      const stream = await navigator.mediaDevices?.getUserMedia?.({ audio: true })
      stream?.getTracks().forEach((track) => track.stop())
    } catch {
      if (sessionRef.current === token) {
        listeningRef.current = false
        setListening(false)
        setError('Permita o microfone para ditar a nota.')
      }
      return
    }
    await new Promise((resolve) => window.setTimeout(resolve, 250))
    if (sessionRef.current !== token) return
    committedRef.current = normalizeSpeech(textRef.current)
    sessionFinalRef.current = ''
    recognition.lang = 'pt-BR'
    recognition.continuous = true
    recognition.interimResults = true
    recognition.maxAlternatives = 1
    recognition.onresult = (event) => {
      if (sessionRef.current !== token) return
      let finalText = ''
      let interimText = ''
      for (let index = 0; index < event.results.length; index += 1) {
        const piece = normalizeSpeech(event.results[index][0]?.transcript)
        if (!piece) continue
        if (event.results[index].isFinal) finalText = mergeSpeech(finalText, piece)
        else interimText = piece
      }
      sessionFinalRef.current = finalText
      const spoken = mergeSpeech(committedRef.current, finalText)
      const shown = interimText ? mergeSpeech(spoken, interimText) : spoken
      textRef.current = shown
      setText(shown)
    }
    recognition.onerror = (event) => {
      if (sessionRef.current !== token) return
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        setError('Permita o microfone para ditar a nota.')
        listeningRef.current = false
        setListening(false)
      } else if (event.error !== 'aborted' && event.error !== 'no-speech') {
        setError('Não foi possível usar o microfone.')
        listeningRef.current = false
        setListening(false)
      }
    }
    recognition.onend = () => {
      if (sessionRef.current !== token) return
      committedRef.current = mergeSpeech(committedRef.current, sessionFinalRef.current)
      sessionFinalRef.current = ''
      textRef.current = committedRef.current
      setText(committedRef.current)
      if (!listeningRef.current) {
        setListening(false)
        return
      }
      window.setTimeout(() => {
        if (sessionRef.current !== token || !listeningRef.current) return
        try {
          recognition.start()
        } catch {
          listeningRef.current = false
          setListening(false)
        }
      }, 200)
    }
    try {
      recognition.start()
      recognitionRef.current = recognition
    } catch {
      if (sessionRef.current === token) {
        listeningRef.current = false
        setListening(false)
        setError('Não foi possível iniciar o microfone.')
      }
    }
  }

  const toggleMic = () => {
    if (listeningRef.current) stopListening()
    else startListening()
  }

  const send = async () => {
    const body = normalizeSpeech(textRef.current)
    if (!body || sending) return
    stopListening()
    textRef.current = ''
    committedRef.current = ''
    const optimistic = {
      id: `tmp-${Date.now()}`,
      body,
      author_name: 'Você',
      created_at: new Date().toISOString(),
      pending: true
    }
    setNotes((current) => [optimistic, ...current])
    setText('')
    setError('')
    setSending(true)
    inputRef.current?.focus()
    try {
      const response = await patientService.addNote(patientId, body)
      setNotes((current) => current.map((item) => (item.id === optimistic.id ? response.data : item)))
      window.dispatchEvent(new Event('wfmed-shift-note'))
    } catch (err) {
      setNotes((current) => current.filter((item) => item.id !== optimistic.id))
      textRef.current = body
      committedRef.current = body
      setText(body)
      setError(err.response?.data?.message || 'Não foi possível enviar a nota')
    } finally {
      setSending(false)
      inputRef.current?.focus()
    }
  }

  return createPortal(
    <div className="patients-note-sheet-layer">
      <button type="button" className="patients-note-sheet-overlay" aria-label="Fechar diário" onClick={onClose} />
      <section className="patients-note-sheet" role="dialog" aria-modal="true" aria-labelledby="patients-note-sheet-title">
        <div className="patients-note-sheet-handle" />
        <header className="patients-note-sheet-head">
          <div>
            <h2 id="patients-note-sheet-title">Diário de Plantão</h2>
            <p>Paciente: {patientName || 'Paciente'}</p>
          </div>
          <button type="button" className="patients-note-sheet-close" aria-label="Fechar" onClick={onClose}>
            <X size={18} />
          </button>
        </header>
        <div className="patients-note-sheet-feed" ref={feedRef}>
          {notes.length === 0 && <p className="patients-muted">Nenhuma nota hoje.</p>}
          {notes.map((note) => (
            <NoteMessage
              key={note.id}
              note={note}
              canChange={owns(note)}
              onUpdate={async (current, body) => {
                const response = await patientService.updateNote(patientId, current.id, body)
                setNotes((list) => list.map((item) => (item.id === current.id ? response.data : item)))
                window.dispatchEvent(new Event('wfmed-shift-note'))
              }}
              onDelete={async (current) => {
                await patientService.deleteNote(patientId, current.id)
                setNotes((list) => list.filter((item) => item.id !== current.id))
                window.dispatchEvent(new Event('wfmed-shift-note'))
              }}
            />
          ))}
        </div>
        {error && <p className="patients-note-sheet-error">{error}</p>}
        <div className="patients-note-sheet-compose">
          <textarea
            ref={inputRef}
            value={text}
            rows={3}
            maxLength={2000}
            placeholder="Digite a ocorrência, intercorrência ou observação..."
            onChange={(event) => {
              const value = event.target.value
              textRef.current = value
              setText(value)
              if (!listeningRef.current) committedRef.current = value
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault()
                send()
              }
            }}
          />
          <div className="patients-note-sheet-actions">
            <button
              type="button"
              className={`patients-note-sheet-mic${listening ? ' is-listening' : ''}`}
              aria-label={listening ? 'Parar ditado' : 'Ditar nota'}
              aria-pressed={listening}
              title={listening ? 'Parar ditado' : 'Ditar nota'}
              onClick={toggleMic}
            >
              <Mic size={18} />
            </button>
            <button type="button" className="patients-btn patients-btn--primary" onClick={send} disabled={sending || !text.trim()}>
              <Send size={15} /> {sending ? 'Enviando...' : 'Enviar'}
            </button>
          </div>
        </div>
      </section>
    </div>,
    document.body
  )
}

export default ShiftNoteSheet
